/**
 * Lenient item validation shared by the find and search fetchers.
 *
 * An item the backend returns that fails its zod schema, or whose provider
 * mapping functions fail, is dropped and reported rather than failing the
 * whole response. The remote lookup id is derived from the parsed item, so an
 * invalid item has no id to attach an error to; from the caller's perspective
 * it is missing. This mirrors the backend adapter (see
 * providers/provider.md, "Invalid items"):
 *
 * - find never fails on invalid items, so a given id is consistently missing
 *   regardless of which other ids it was batched with;
 * - search throws {@link RemoteLookupInvalidItemsError} when a non-empty page
 *   had every item rejected, so a broken provider (or a schema skew after a
 *   deploy) surfaces as a failed request instead of an empty dropdown.
 *
 * @owner @Appboy/messaging-experience
 */
import type { z } from "zod";

import { FetchRequestError, reportUnexpectedError } from "@lib/errors";

import type { RemoteLookupProvider } from "./providers/types";
import type {
  InvalidItem,
  LenientParseResult,
  ParseItemOutcome,
  ParseItemsLenientlyParams,
  ParsedItem,
  RemoteLookupMethod,
} from "./types";

const maxIssuesInMessage = 3;

/**
 * Thrown when every item on a non-empty search page failed validation.
 * Carries a 4xx status so react-query does not retry a response that would
 * fail the same way again.
 */
export class RemoteLookupInvalidItemsError extends FetchRequestError<{ failures: InvalidItem[] }> {
  constructor(providerType: string, method: RemoteLookupMethod, failures: InvalidItem[]) {
    super(
      422,
      `All ${failures.length} items returned from the ${providerType} ${method} failed validation; ${describeFailure(
        failures[0]
      )}`,
      { failures }
    );
    this.name = "RemoteLookupInvalidItemsError";
  }
}

/**
 * Validates each returned item independently and derives its id, collecting
 * failures instead of throwing. An item is rejected when it fails the item
 * schema, when `mapItemToId` throws, when the mapped id fails the id schema,
 * or when `mapIdToString` throws or returns something other than a non-empty
 * string, so a runtime bug in a provider's mapping functions drops that item
 * rather than the whole response. Dropped items are reported once per call.
 */
export function parseItemsLeniently<Id, Item>({
  provider,
  providerType,
  method,
  rawItems,
  failWhenAllInvalid,
}: ParseItemsLenientlyParams<Id, Item>): LenientParseResult<Id, Item> {
  const items: ParsedItem<Id, Item>[] = [];
  const failures: InvalidItem[] = [];
  rawItems.forEach((raw, index) => {
    const outcome = parseItem(provider, raw);
    if ("failure" in outcome) {
      failures.push({ index, ...outcome.failure });
    } else {
      items.push(outcome.parsed);
    }
  });
  if (failWhenAllInvalid && rawItems.length > 0 && items.length === 0) {
    throw new RemoteLookupInvalidItemsError(providerType, method, failures);
  }
  reportInvalidItems(providerType, method, failures, rawItems.length);
  return { items, failures };
}

function parseItem<Id, Item>(
  provider: RemoteLookupProvider<Id, Item, unknown>,
  raw: unknown
): ParseItemOutcome<Id, Item> {
  const itemResult = provider.validators.item.safeParse(raw);
  if (!itemResult.success) {
    return { failure: { stage: "item", reason: describeIssues(itemResult.error.issues) } };
  }
  const item = itemResult.data;

  let id: Id;
  try {
    id = provider.mapItemToId(item);
  } catch (error) {
    return { failure: { stage: "mapItemToId", reason: `threw ${describeError(error)}` } };
  }
  const idResult = provider.validators.id.safeParse(id);
  if (!idResult.success) {
    return { failure: { stage: "id", reason: describeIssues(idResult.error.issues) } };
  }

  let idString: unknown;
  try {
    idString = provider.mapIdToString(id);
  } catch (error) {
    return { failure: { stage: "mapIdToString", reason: `threw ${describeError(error)}` } };
  }
  if (typeof idString !== "string" || idString.length === 0) {
    return {
      failure: { stage: "mapIdToString", reason: `returned ${JSON.stringify(idString)}, not a non-empty string` },
    };
  }

  return { parsed: { item, id, idString } };
}

function describeIssues(issues: z.ZodIssue[]): string {
  return issues
    .slice(0, maxIssuesInMessage)
    .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
    .join("; ");
}

function describeError(error: unknown): string {
  return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

/**
 * One-line description of a failure, safe for an error message.
 */
export function describeFailure(failure: InvalidItem | undefined): string {
  if (!failure) {
    return "no failure details";
  }
  return `item ${failure.index} (${failure.stage}): ${failure.reason}`;
}

/**
 * Reports dropped items to the monitoring pipeline once per response, with
 * the first failure as an example. The response still succeeds; this is what
 * makes a silently-missing item findable.
 */
export function reportInvalidItems(
  providerType: string,
  method: RemoteLookupMethod,
  failures: InvalidItem[],
  total: number
): void {
  if (failures.length === 0) {
    return;
  }
  const error = new Error(
    `${failures.length} of ${total} items returned from the ${providerType} ${method} failed validation and were dropped; ${describeFailure(
      failures[0]
    )}`
  );
  error.name = "RemoteLookupInvalidItemsDropped";
  // Instrumented because: a dropped item renders as "Unknown value" and is
  // otherwise invisible; this usually means a backend/frontend schema skew
  // after a deploy, or a bug in a provider's mapping functions. One report
  // per response, not per item.
  // If you see this: compare the failing stage and reason against the
  // provider's cross-type schema and mapping functions; regenerate or
  // redeploy the frontend, or fix the provider.
  // Remove when: never (backstop for schema drift).
  reportUnexpectedError(error, `Remote lookup ${method} dropped invalid items:`);
}
